import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIME } from '@hygge/shared';
import type {
  MyDashboardWidget,
  MyTimeOffData,
  BirthdaysAnniversariesData,
  WhosOutTodayData,
  ProbationReviewsData,
  HeadcountThisMonthData,
  OpenVacanciesData,
  UpcomingInterviewsData,
  StalledCandidatesData,
  PipelineFunnelData,
  TeamTodayData,
  TeamWorkloadData,
  TeamRequestsData,
  MyProjectsData,
  RoleDashboardWidgetsResponse,
} from '@hygge/shared';
import api from '@/lib/api';

export const dashboardKeys = {
  all: ['dashboard'] as const,
  myWidgets: () => [...dashboardKeys.all, 'my-widgets'] as const,
  widget: (key: string) => [...dashboardKeys.all, 'widget', key] as const,
};

/** Ordered widget list for the signed-in user's dashboard. */
export function useMyDashboard() {
  return useQuery<MyDashboardWidget[]>({
    queryKey: dashboardKeys.myWidgets(),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/my-widgets');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `myTimeOff` widget data for the signed-in employee. */
export function useMyTimeOff() {
  return useQuery<MyTimeOffData>({
    queryKey: dashboardKeys.widget('myTimeOff'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/my-time-off');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `birthdaysAnniversaries` widget — today's celebrants, company-wide. */
export function useBirthdaysAnniversaries() {
  return useQuery<BirthdaysAnniversariesData>({
    queryKey: dashboardKeys.widget('birthdaysAnniversaries'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/birthdays-anniversaries');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `whosOutToday` widget — everyone on approved leave today, company-wide. */
export function useWhosOutToday() {
  return useQuery<WhosOutTodayData>({
    queryKey: dashboardKeys.widget('whosOutToday'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/whos-out-today');
      return data;
    },
    staleTime: CACHE_TIME.SHORT, // volatile "today" data — approvals change through the day
  });
}

/** `probationReviews` widget — probation ends (≤10d) + comp reviews due (≤30d). */
export function useProbationReviews() {
  return useQuery<ProbationReviewsData>({
    queryKey: dashboardKeys.widget('probationReviews'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/probation-reviews');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `headcountThisMonth` widget — onboarding + offboarded this calendar month. */
export function useHeadcountThisMonth() {
  return useQuery<HeadcountThisMonthData>({
    queryKey: dashboardKeys.widget('headcountThisMonth'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/headcount-this-month');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `openVacancies` widget — the caller's active vacancies, longest-open first. */
export function useOpenVacancies() {
  return useQuery<OpenVacanciesData>({
    queryKey: dashboardKeys.widget('openVacancies'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/open-vacancies');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `upcomingInterviews` widget — the caller's future scheduled interviews. */
export function useUpcomingInterviews() {
  return useQuery<UpcomingInterviewsData>({
    queryKey: dashboardKeys.widget('upcomingInterviews'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/upcoming-interviews');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `stalledCandidates` widget — HIRING-bucket candidates stalled 7+ days. */
export function useStalledCandidates() {
  return useQuery<StalledCandidatesData>({
    queryKey: dashboardKeys.widget('stalledCandidates'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/stalled-candidates');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `pipelineFunnel` widget — the caller's candidates grouped by lifecycle bucket. */
export function usePipelineFunnel() {
  return useQuery<PipelineFunnelData>({
    queryKey: dashboardKeys.widget('pipelineFunnel'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/pipeline-funnel');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `teamTodayManager` widget — the manager's direct reports on leave today. */
export function useTeamTodayManager() {
  return useQuery<TeamTodayData>({
    queryKey: dashboardKeys.widget('teamTodayManager'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/team-today-manager');
      return data;
    },
    staleTime: CACHE_TIME.SHORT, // volatile "today" data
  });
}

/** `teamWorkload` widget — recruiting-team members + their open-vacancy load. */
export function useTeamWorkload() {
  return useQuery<TeamWorkloadData>({
    queryKey: dashboardKeys.widget('teamWorkload'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/team-workload');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/** `teamRequestsManager` widget — direct reports' pending absence requests. */
export function useTeamRequestsManager() {
  return useQuery<TeamRequestsData>({
    queryKey: dashboardKeys.widget('teamRequestsManager'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/team-requests-manager');
      return data;
    },
    staleTime: CACHE_TIME.SHORT, // volatile — shrinks as requests are decided
  });
}

/** `myProjects` widget — the caller's active projects they manage; a plain list, no health status. */
export function useMyProjects() {
  return useQuery<MyProjectsData>({
    queryKey: dashboardKeys.widget('myProjects'),
    queryFn: async () => {
      const { data } = await api.get('/dashboard/widgets/my-projects');
      return data;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Approve / reject a team member's absence request inline, reusing the
 * existing `/absence-requests/:id/{approve,reject}` endpoints. On success, refresh every
 * dashboard widget — a decision changes the requests list, who's-out and team-today — and the
 * Absences views the same decision changes, so a request decided here doesn't leave a stale
 * Absences page behind (the reverse of what the central invalidation in `main.tsx` does for
 * the dashboard).
 */
export function useDecideAbsenceRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      decision,
    }: {
      requestId: string;
      decision: 'approve' | 'reject';
      /** Requester — scopes the per-employee absence keys invalidated on success. */
      employeeId: string;
      // Send an explicit empty body — the endpoint reads `body.comment`, so a
      // bodyless POST would 500. (Comment is optional; the widget doesn't collect one.)
    }) => api.post(`/absence-requests/${requestId}/${decision}`, {}),
    onSuccess: (_data, { employeeId }) =>
      Promise.all([
        // `cancelRefetch: false`: the central `MutationCache.onSuccess` in `main.tsx` runs first
        // and has already invalidated `['dashboard']`, so those widget refetches are in flight by
        // now — the default would abort and re-issue every one that already holds data. Awaiting
        // the round already running keeps the buttons disabled until the decided row is gone,
        // while issuing half as many HTTP requests.
        queryClient.invalidateQueries({ queryKey: dashboardKeys.all }, { cancelRefetch: false }),
        // The set Absences → Requests invalidates for its own approve/reject
        // (`absences.request.tsx`), so deciding here or there leaves the requests lists, the
        // pending-approvals badge, the calendar and the requester's profile Absences tab in the
        // same state — plus the balances overview, which a decision also changes and which that
        // page happens to miss. Spelled out as literals because the absences module exports no
        // key factory for them (the one in `use-employee-policies.ts` backs unused hooks and
        // differently shaped keys); each key here backs a live query.
        queryClient.invalidateQueries({ queryKey: ['absence-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] }),
        queryClient.invalidateQueries({ queryKey: ['balances-overview'] }),
        queryClient.invalidateQueries({
          queryKey: ['employee-assigned-absence-types', employeeId],
        }),
        queryClient.invalidateQueries({ queryKey: ['employee-policies', employeeId] }),
        queryClient.invalidateQueries({ queryKey: ['employee-absences', employeeId] }),
      ]),
    // The widget shows the failure inline (TeamRequestsList), so opt out of the global
    // error toast (main.tsx MutationCache) to avoid surfacing the same message twice.
    meta: { suppressGlobalToast: true },
  });
}

/** Query key for a role's dashboard-widget configuration (Configurator). */
export const roleDashboardWidgetsKey = (roleId: string) =>
  ['role-dashboard-widgets', roleId] as const;

/** A role's assigned + available dashboard widgets, for the Dashboard Configurator. */
export function useRoleDashboardWidgets(roleId: string | null) {
  return useQuery<RoleDashboardWidgetsResponse>({
    queryKey: roleDashboardWidgetsKey(roleId ?? ''),
    queryFn: async () => {
      const { data } = await api.get(`/roles/${roleId}/dashboard-widgets`);
      return data;
    },
    enabled: !!roleId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Replace a role's dashboard widgets. Seeds the Configurator cache with the saved
 * result and invalidates every dashboard widget query — a user on that role sees the
 * new layout on next fetch.
 */
export function useUpdateRoleDashboardWidgets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, widgetKeys }: { roleId: string; widgetKeys: string[] }) => {
      const { data } = await api.put(`/roles/${roleId}/dashboard-widgets`, { widgetKeys });
      return data as RoleDashboardWidgetsResponse;
    },
    onSuccess: (data, { roleId }) => {
      // Seed the Configurator's cache with the server's canonical result so the
      // "Unsaved changes" flag doesn't flicker on during the post-save refetch window.
      queryClient.setQueryData(roleDashboardWidgetsKey(roleId), data);
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
    // The Configurator renders save failures inline (saveError), so opt out of the global
    // error toast (main.tsx MutationCache) to avoid surfacing the same message twice.
    meta: { suppressGlobalToast: true },
  });
}
